import { normalizeCepDigits } from "./cepLookup";

export const BRAZILIAN_UFS = Object.freeze([
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA",
  "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
]);

const UF_BY_STATE_NAME = Object.freeze({
  ACRE: "AC",
  ALAGOAS: "AL",
  AMAPA: "AP",
  AMAZONAS: "AM",
  BAHIA: "BA",
  CEARA: "CE",
  "DISTRITO FEDERAL": "DF",
  "ESPIRITO SANTO": "ES",
  GOIAS: "GO",
  MARANHAO: "MA",
  "MATO GROSSO": "MT",
  "MATO GROSSO DO SUL": "MS",
  "MINAS GERAIS": "MG",
  PARA: "PA",
  PARAIBA: "PB",
  PARANA: "PR",
  PERNAMBUCO: "PE",
  PIAUI: "PI",
  "RIO DE JANEIRO": "RJ",
  "RIO GRANDE DO NORTE": "RN",
  "RIO GRANDE DO SUL": "RS",
  RONDONIA: "RO",
  RORAIMA: "RR",
  "SANTA CATARINA": "SC",
  "SAO PAULO": "SP",
  SERGIPE: "SE",
  TOCANTINS: "TO",
});

// Salesforce BillingState is free text ("ES", "Espírito Santo", "MINAS GERAIS"...).
// Returns the UF sigla, or "" when the value is not a recognisable Brazilian state.
export const normalizeUf = (value) => {
  if (typeof value !== "string") return "";
  const key = value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (BRAZILIAN_UFS.includes(key)) return key;
  return UF_BY_STATE_NAME[key] || "";
};

// Salesforce stores "street, number" in one 150-character field.
export const STREET_WITH_NUMBER_MAX_LENGTH = 150;

export const ADDRESS_MAX_LENGTHS = Object.freeze({
  street: 150,
  houseNumber: 50,
  complement: 100,
  neighborhood: 100,
  city: 40,
});

export const NO_HOUSE_NUMBER = "S/N";

export const ADDRESS_FIELDS = Object.freeze([
  "street",
  "houseNumber",
  "complement",
  "neighborhood",
  "city",
  "state",
  "postalCode",
]);

// The backend masks some address fields (e.g. "***", "****") until unmasked.
export const isMaskedValue = (value) => typeof value === "string" && value.includes("*");

export const formatCep = (value) => {
  if (value == null || value === "") return "";
  if (isMaskedValue(value)) return value;
  const digits = String(value).replace(/\D/g, "");
  if (digits.length === 8) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  return String(value);
};

// Progressive mask for the CEP input: "35010" -> "35010", "350100" -> "35010-0".
export const maskCepInput = (value) => {
  const digits = normalizeCepDigits(value);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
};

export const isNoHouseNumber = (value) =>
  typeof value === "string" && value.trim().toUpperCase().replace(/\s/g, "") === NO_HOUSE_NUMBER;

const editableValue = (value) => (typeof value === "string" && !isMaskedValue(value) ? value : "");

// Builds the modal's initial form state, dropping masked values so they are never resubmitted.
export const toAddressForm = (details = {}) => {
  const houseNumber = editableValue(details.houseNumber);
  const state = normalizeUf(editableValue(details.state));
  return {
    postalCode: maskCepInput(editableValue(details.postalCode)),
    street: editableValue(details.street),
    houseNumber: isNoHouseNumber(houseNumber) ? "" : houseNumber,
    noNumber: isNoHouseNumber(houseNumber),
    complement: editableValue(details.complement),
    neighborhood: editableValue(details.neighborhood),
    city: editableValue(details.city),
    state,
  };
};

export const buildAddressPayload = (form) => ({
  postalCode: normalizeCepDigits(form.postalCode),
  street: form.street.trim(),
  houseNumber: form.noNumber ? NO_HOUSE_NUMBER : form.houseNumber.trim(),
  complement: form.complement.trim(),
  neighborhood: form.neighborhood.trim(),
  city: form.city.trim(),
  state: form.state.trim().toUpperCase(),
});

// Returns a map of field -> i18n error key (under clientDetails.address.errors).
export const validateAddressForm = (form) => {
  const errors = {};
  const required = (field) => {
    if (!String(form[field] ?? "").trim()) errors[field] = "required";
  };
  const maxLength = (field) => {
    if (!errors[field] && String(form[field] ?? "").trim().length > ADDRESS_MAX_LENGTHS[field]) {
      errors[field] = "tooLong";
    }
  };

  if (normalizeCepDigits(form.postalCode).length === 0) errors.postalCode = "required";
  else if (normalizeCepDigits(form.postalCode).length !== 8) errors.postalCode = "cepLength";

  required("street");
  maxLength("street");
  if (!form.noNumber) {
    required("houseNumber");
    maxLength("houseNumber");
  }
  if (!errors.street && !errors.houseNumber) {
    const number = form.noNumber ? NO_HOUSE_NUMBER : String(form.houseNumber ?? "").trim();
    const combined = `${String(form.street ?? "").trim()}, ${number}`;
    if (combined.length > STREET_WITH_NUMBER_MAX_LENGTH) errors.street = "streetWithNumberTooLong";
  }
  maxLength("complement");
  required("neighborhood");
  maxLength("neighborhood");
  required("city");
  maxLength("city");
  if (!String(form.state ?? "").trim()) errors.state = "required";
  else if (!BRAZILIAN_UFS.includes(form.state.trim().toUpperCase())) errors.state = "invalidUf";

  return errors;
};

// `addressEdit` comes from /features; REACT_APP_FORCE_ADDRESS_EDIT only applies in development.
export const isAddressEditEnabled = (features, env = process.env) =>
  features?.addressEdit === true
  || (env.NODE_ENV === "development" && env.REACT_APP_FORCE_ADDRESS_EDIT === "true");

// The backend sends `canEditAddress` (flag on AND caller owns the party); a 403 on save also hides it.
// The dev force only applies while the backend does not send the field yet.
export const canEditPartyAddress = (features, details, { forbidden = false } = {}, env = process.env) => {
  if (!isAddressEditEnabled(features, env) || forbidden) return false;
  if (details?.canEditAddress === true) return true;
  return details?.canEditAddress === undefined
    && env.NODE_ENV === "development" && env.REACT_APP_FORCE_ADDRESS_EDIT === "true";
};
